/** The two controllers the loop drives.
 *
 * `PlayerController` (3.7) is one interface with one method, and both the human
 * and the computer implement it. That is the point of I9: the human's command
 * and the AI's command reach `executeCommand` down the same pipe, so a command
 * the UI can issue is a command the AI could have issued and vice versa.
 */

import type { GameCommand, PlayerObservation } from "@game/core";
import type { PlayerController } from "@game/ai";
import {
  chooseBattleCommand, createAiController,
  type AiController, type DifficultyId, type DifficultyProfile,
} from "@game/ai";
import type { ContentRegistry, Resources } from "@game/core";
import { STARTING_RESOURCES } from "@game/core";

/**
 * The human. `chooseCommand` parks until the UI offers something; the UI is the
 * only thing that resolves it, and an abort resolves it with an END_TURN so a
 * loop shutdown can never hang on a player who walked away.
 */
export class HumanController implements PlayerController {
  readonly kind = "human" as const;

  private waiting: ((cmd: GameCommand) => void) | null = null;
  private queued: GameCommand[] = [];
  private observation: PlayerObservation | null = null;
  private auto: AutoBattle | null = null;

  /** True while the loop is parked on this player. The UI greys itself when not. */
  get awaiting(): boolean { return this.waiting !== null; }

  /** What the loop last showed this player. Debug only. */
  get lastObservation(): PlayerObservation | null { return this.observation; }

  /**
   * Hand this seat's BATTLE turns to the computer, or take them back.
   *
   * Quick Combat is not a second way to fight: it is this seat answering with
   * the plan `chooseBattleCommand` would have made, down the same pipe, so the
   * fight is journalled, hashed and replayable exactly as a hand-played one is
   * (I9). Nothing else about the seat changes - the adventure turn is still the
   * player's, and a battle that ends returns the board to him.
   */
  setAutoBattle(auto: AutoBattle | null): void {
    this.auto = auto;
    // The loop may already be parked on this seat waiting for a click; without
    // this the fight would not start moving until the player pressed something,
    // which is the one thing Quick Combat exists to avoid.
    const obs = this.observation;
    if (auto === null || obs === null || this.waiting === null) return;
    const cmd = autoCommandFor(auto, obs);
    if (cmd !== null) this.offer(cmd);
  }

  get autoBattle(): boolean { return this.auto !== null; }

  chooseCommand(obs: PlayerObservation, signal: AbortSignal): Promise<GameCommand> {
    this.observation = obs;
    const next = this.queued.shift();
    if (next !== undefined) return Promise.resolve(next);

    // Asked before the seat parks: an auto-fought battle never waits for a
    // click. Only a battle - `autoCommandFor` answers null on the adventure
    // map, so the player keeps his own turn.
    if (this.auto !== null) {
      const cmd = autoCommandFor(this.auto, obs);
      if (cmd !== null) return Promise.resolve(cmd);
    }

    return new Promise<GameCommand>((resolve) => {
      const done = (cmd: GameCommand): void => {
        if (this.waiting === null) return;
        this.waiting = null;
        signal.removeEventListener("abort", onAbort);
        resolve(cmd);
      };
      const onAbort = (): void => {
        const self = obs.self;
        if (self !== null) done({ type: "END_TURN", player: self.id });
      };
      this.waiting = done;
      if (signal.aborted) onAbort();
      else signal.addEventListener("abort", onAbort, { once: true });
    });
  }

  /**
   * The UI's door. Buffered when the loop is not parked yet, so a click that
   * lands a frame early is not silently dropped.
   *
   * The buffer is load-bearing rather than defensive. `AppModel.seatFor` hands
   * a command to the seat the KERNEL says may act, which during an animation is
   * a seat the loop has not asked yet - it is still inside `present` for the
   * previous actor. Parking it here is what makes the order run through the
   * loop, and so through `present`, instead of overtaking the batch the player
   * is watching.
   */
  offer(command: GameCommand): void {
    const w = this.waiting;
    if (w === null) { this.queued.push(command); return; }
    w(command);
  }

  /** Drop anything buffered - used when a turn ends or a screen is left. */
  clear(): void { this.queued = []; }
}

/** What a seat needs to fight for itself: the rules, and how well to play. */
export interface AutoBattle {
  readonly content: ContentRegistry;
  readonly difficulty: DifficultyProfile;
}

/**
 * The computer's move for this seat, or null when there is nothing to fight.
 *
 * Null on the adventure map, null while the battle belongs to somebody else,
 * and null when the planner has no move - all three fall back to the player,
 * because a Quick Combat that answered END_TURN on the map would play the
 * game for him.
 */
function autoCommandFor(auto: AutoBattle, obs: PlayerObservation): GameCommand | null {
  const battle = obs.battle;
  const me = obs.self?.id;
  if (battle === null || me === undefined) return null;
  const side = battle.heroes[0].owner === me ? 0 : battle.heroes[1].owner === me ? 1 : null;
  if (side === null) return null;
  const cmd = chooseBattleCommand(battle, side, auto.content, auto.difficulty);
  return cmd === null ? null : { type: "BATTLE", cmd };
}

export interface AiSetup {
  readonly content: ContentRegistry;
  readonly difficulty?: DifficultyId;
}

export function makeAiController(setup: AiSetup): AiController {
  return createAiController({
    content: setup.content,
    difficulty: setup.difficulty ?? "normal",
  });
}

/**
 * The one difficulty ladder, in the order the New Game screen prints it.
 *
 * The kernel's purse ids (`StartingPurseId`) and the AI's profile ids
 * (`DifficultyId`) are the same four names on purpose: a difficulty level is
 * one choice with two halves - what the human starts with, and how well the
 * computer plays - and two ladders that could drift apart would be two
 * difficulty settings wearing one label.
 */
export const DIFFICULTY_LADDER: readonly DifficultyId[] =
  Object.freeze(["easy", "normal", "hard", "impossible"] as const);

/** The screen's index (0..3) into the ladder. Out of range clamps. */
export function difficultyIdFor(level: number): DifficultyId {
  const i = Number.isFinite(level) ? Math.trunc(level) : 1;
  return DIFFICULTY_LADDER[i < 0 ? 0 : i >= DIFFICULTY_LADDER.length ? DIFFICULTY_LADDER.length - 1 : i]
    ?? "normal";
}

/**
 * What the HUMAN starts the game with at this level - The classic handicap, and
 * the only bonus a difficulty level is allowed to hand anybody.
 *
 * Computer players are never passed this: they take `newGame`'s default, which
 * is the Normal purse, at every level. The AI gets harder by searching deeper
 * (`packages/ai/src/difficulty.ts`), never by being handed resources it did not
 * earn (I9).
 */
export function startingResourcesFor(level: number): Resources {
  return STARTING_RESOURCES[difficultyIdFor(level)];
}
